import { useEffect, useState } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, isFriday, addDays, startOfDay } from 'date-fns';
import { ChevronLeft, ChevronRight, Loader2, Calendar, Users, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingsManager() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState([]);
  const [renters, setRenters] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [currentMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const month = format(currentMonth, 'MM');
      const year = format(currentMonth, 'yyyy');
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [availRes, rentersRes, inquiriesRes] = await Promise.all([
        axios.get(`${API}/availability?month=${month}&year=${year}`),
        axios.get(`${API}/renters`, { headers }),
        axios.get(`${API}/inquiries`, { headers })
      ]);
      
      setAvailability(availRes.data);
      setRenters(rentersRes.data);
      setInquiries(inquiriesRes.data.filter(i => i.status === 'pending'));
    } catch (error) {
      toast.error('Failed to load availability');
    } finally {
      setLoading(false);
    }
  };

  const getDateStatus = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const found = availability.find(a => a.date === dateStr);
    return found?.status || 'available';
  };

  const getDateInfo = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const found = availability.find(a => a.date === dateStr);
    if (!found) return null;
    
    if (found.booking_id || found.renter_id) {
      const renter = renters.find(r => r.id === (found.booking_id || found.renter_id));
      if (renter) {
        return { type: 'renter', name: `${renter.first_name} ${renter.last_name}`, id: renter.id };
      }
    }
    if (found.inquiry_id) {
      const inquiry = inquiries.find(i => i.id === found.inquiry_id);
      if (inquiry) {
        return { type: 'inquiry', name: `${inquiry.first_name} ${inquiry.last_name}`, id: inquiry.id };
      }
    }
    return null;
  };

  // Check if a Friday is a checkout turnover (guest leaving AM, property available PM)
  // Returns the status type ('booked' or 'pending') or false
  const getCheckoutTurnoverType = (date) => {
    if (!isFriday(date)) return false;
    
    const prevDay = addDays(date, -1);
    const prevDayStr = format(prevDay, 'yyyy-MM-dd');
    const prevDayEntry = availability.find(a => a.date === prevDayStr);
    const prevDayStatus = prevDayEntry?.status;
    
    const nextDay = addDays(date, 1);
    const nextDayStr = format(nextDay, 'yyyy-MM-dd');
    const nextDayStatus = availability.find(a => a.date === nextDayStr)?.status;
    
    const thursdayWasBooked = prevDayStatus === 'booked' || prevDayStatus === 'pending';
    const saturdayNotBooked = nextDayStatus !== 'booked' && nextDayStatus !== 'pending';
    
    if (thursdayWasBooked && saturdayNotBooked) {
      return prevDayStatus; // Return 'booked' or 'pending'
    }
    return false;
  };

  // Check if a Friday is a checkin turnover (property available AM, guest arriving PM)
  // Returns the status type ('booked' or 'pending') or false
  const getCheckinTurnoverType = (date) => {
    if (!isFriday(date)) return false;
    
    const prevDay = addDays(date, -1);
    const prevDayStr = format(prevDay, 'yyyy-MM-dd');
    const prevDayStatus = availability.find(a => a.date === prevDayStr)?.status;
    
    const nextDay = addDays(date, 1);
    const nextDayStr = format(nextDay, 'yyyy-MM-dd');
    const nextDayEntry = availability.find(a => a.date === nextDayStr);
    const nextDayStatus = nextDayEntry?.status;
    
    const thursdayNotBooked = prevDayStatus !== 'booked' && prevDayStatus !== 'pending';
    const saturdayIsBooked = nextDayStatus === 'booked' || nextDayStatus === 'pending';
    
    if (thursdayNotBooked && saturdayIsBooked) {
      return nextDayStatus; // Return 'booked' or 'pending'
    }
    return false;
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOfWeek = monthStart.getDay();
  const emptyDays = Array(startDayOfWeek).fill(null);

  // Create sets for fast lookup
  const bookedDateSet = new Set(
    availability.filter(a => a.status === 'booked').map(a => a.date)
  );
  const pendingDateSet = new Set(
    availability.filter(a => a.status === 'pending').map(a => a.date)
  );

  // Get current month's bookings for the sidebar
  const currentMonthBookings = renters.filter(r => {
    if (!r.check_in_date || !r.check_out_date) return false;
    const checkIn = new Date(r.check_in_date);
    const checkOut = new Date(r.check_out_date);
    return (checkIn <= monthEnd && checkOut >= monthStart);
  });

  const currentMonthInquiries = inquiries.filter(i => {
    if (!i.check_in_date || !i.check_out_date) return false;
    const checkIn = new Date(i.check_in_date);
    const checkOut = new Date(i.check_out_date);
    return (checkIn <= monthEnd && checkOut >= monthStart);
  });

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Calendar className="w-6 h-6" />
          Availability Calendar
        </h1>
        <p className="text-gray-600 mt-1">
          View bookings from renters and pending inquiries
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-3 bg-white rounded-xl shadow-sm border p-6">
          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded bg-green-100 border-2 border-green-600" />
              <span className="text-gray-600">Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded bg-red-500" />
              <span className="text-gray-600">Booked</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded bg-amber-400" />
              <span className="text-gray-600">Pending</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded" style={{
                background: 'linear-gradient(135deg, #F59E0B 0%, #F59E0B 50%, #E8F5E9 50%, #E8F5E9 100%)'
              }} />
              <span className="text-gray-600">Pending Checkout</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded" style={{
                background: 'linear-gradient(135deg, #E8F5E9 0%, #E8F5E9 50%, #F59E0B 50%, #F59E0B 100%)'
              }} />
              <span className="text-gray-600">Pending Check-in</span>
            </div>
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold text-gray-900">
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
            <button
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : (
            <>
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Days Grid */}
              <div className="grid grid-cols-7 gap-2">
                {emptyDays.map((_, idx) => (
                  <div key={`empty-${idx}`} className="aspect-square" />
                ))}
                {days.map(day => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const status = getDateStatus(day);
                  const info = getDateInfo(day);
                  const isPast = day < startOfDay(new Date());
                  const dayIsFriday = isFriday(day);
                  const checkoutType = getCheckoutTurnoverType(day);
                  const checkinType = getCheckinTurnoverType(day);

                  // Determine styling
                  let bgStyle = {};
                  let bgClass = 'bg-green-50 border-green-200';
                  let textClass = 'text-gray-700';

                  // Colors: Red (#DC2626) for booked, Amber (#F59E0B) for pending, Green (#E8F5E9) for available
                  if (checkoutType) {
                    // Checkout turnover - booking color on top-left, green on bottom-right
                    const bookingColor = checkoutType === 'pending' ? '#F59E0B' : '#DC2626';
                    bgStyle = {
                      background: `linear-gradient(135deg, ${bookingColor} 0%, ${bookingColor} 50%, #E8F5E9 50%, #E8F5E9 100%)`
                    };
                    bgClass = '';
                    textClass = 'text-gray-900 font-semibold';
                  } else if (checkinType) {
                    // Checkin turnover - green on top-left, booking color on bottom-right
                    const bookingColor = checkinType === 'pending' ? '#F59E0B' : '#DC2626';
                    bgStyle = {
                      background: `linear-gradient(135deg, #E8F5E9 0%, #E8F5E9 50%, ${bookingColor} 50%, ${bookingColor} 100%)`
                    };
                    bgClass = '';
                    textClass = 'text-gray-900 font-semibold';
                  } else if (status === 'booked') {
                    bgClass = 'bg-red-500';
                    textClass = 'text-white';
                  } else if (status === 'pending') {
                    bgClass = 'bg-amber-400';
                    textClass = 'text-white';
                  } else if (dayIsFriday) {
                    bgClass = 'bg-green-100 border-2 border-green-600';
                    textClass = 'text-green-800 font-semibold';
                  }

                  return (
                    <div
                      key={dateStr}
                      className={`
                        aspect-square p-1 rounded-lg border text-sm relative group
                        ${isPast ? 'opacity-40' : ''}
                        ${bgClass}
                        ${textClass}
                      `}
                      style={bgStyle}
                      title={info ? `${info.type === 'renter' ? 'Renter' : 'Inquiry'}: ${info.name}` : ''}
                    >
                      <span className="font-medium">{format(day, 'd')}</span>
                      {dayIsFriday && (
                        <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 text-[8px] opacity-60">Fri</span>
                      )}
                      {info && (
                        <div className="absolute inset-0 bg-black/70 text-white text-xs p-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-center items-center text-center">
                          <span className="font-medium truncate w-full">{info.name}</span>
                          <span className="text-[10px] opacity-80">{info.type === 'renter' ? 'Booked' : 'Pending'}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Info Note */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> This calendar automatically reflects bookings from your Renters and pending Inquiries. 
              To add or modify bookings, go to <Link to="/admin/renters" className="underline hover:text-blue-600">Renters</Link> or <Link to="/admin/inquiries" className="underline hover:text-blue-600">Inquiries</Link>.
            </p>
          </div>
        </div>

        {/* Sidebar - This Month's Bookings */}
        <div className="lg:col-span-1 space-y-6">
          {/* Confirmed Bookings */}
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Users className="w-4 h-4 text-emerald-600" />
              Confirmed Renters
            </h3>
            {currentMonthBookings.length === 0 ? (
              <p className="text-sm text-gray-500">No bookings this month</p>
            ) : (
              <div className="space-y-3">
                {currentMonthBookings.map(renter => (
                  <Link
                    key={renter.id}
                    to={`/admin/renters/${renter.id}`}
                    className="block p-3 bg-red-50 rounded-lg border border-red-200 hover:bg-red-100 transition-colors"
                  >
                    <p className="font-medium text-gray-900">{renter.first_name} {renter.last_name}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      {format(new Date(renter.check_in_date), 'MMM d')} - {format(new Date(renter.check_out_date), 'MMM d, yyyy')}
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Pending Inquiries */}
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Clock className="w-4 h-4 text-amber-500" />
              Pending Inquiries
            </h3>
            {currentMonthInquiries.length === 0 ? (
              <p className="text-sm text-gray-500">No pending inquiries this month</p>
            ) : (
              <div className="space-y-3">
                {currentMonthInquiries.map(inquiry => (
                  <Link
                    key={inquiry.id}
                    to={`/admin/inquiries/${inquiry.id}`}
                    className="block p-3 bg-amber-50 rounded-lg border border-amber-200 hover:bg-amber-100 transition-colors"
                  >
                    <p className="font-medium text-gray-900">{inquiry.first_name} {inquiry.last_name}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      {format(new Date(inquiry.check_in_date), 'MMM d')} - {format(new Date(inquiry.check_out_date), 'MMM d, yyyy')}
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
